import type { IntegrationProfileConfig } from '@steerfi/shared/types/jsonb/integration-profile.types';
import { config } from '@/config';
import { getAuthToken } from './keycloak-auth';

export class IntegrationProfileApiError extends Error {
  status: number;
  body?: Record<string, unknown>;

  constructor(message: string, status: number, body?: Record<string, unknown>) {
    super(message);
    this.name = 'IntegrationProfileApiError';
    this.status = status;
    if (body !== undefined) {
      this.body = body;
    }
  }
}

async function ipRequest<T>(partyId: string, path: string, options: RequestInit = {}): Promise<T> {
  const url = `${config.api.baseURL}/internal/providers/${partyId}/integration-profiles${path}`;
  const token = await getAuthToken();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include',
    cache: 'no-store',
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    const msg =
      (typeof body.message === 'string' && body.message) ||
      (typeof body.error === 'string' && body.error) ||
      `HTTP ${response.status}`;
    throw new IntegrationProfileApiError(msg, response.status, body);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  if (!text) {
    return undefined as T;
  }
  const parsed = JSON.parse(text) as Record<string, unknown>;
  if (
    parsed &&
    typeof parsed === 'object' &&
    'success' in parsed &&
    'data' in parsed &&
    !('pagination' in parsed)
  ) {
    return (parsed as { data: T }).data;
  }
  return parsed as T;
}

const buildQuery = (params: Record<string, unknown>): string => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  const s = searchParams.toString();
  return s ? `?${s}` : '';
};

export interface IntegrationProfileApiRow {
  id: string;
  partyId: string;
  profileName: string;
  integrationTypeId: number;
  statusId: number;
  environment: string;
  configJson: IntegrationProfileConfig | null;
  created_at: string;
  updated_at: string;
}

export interface IntegrationProfileListQuery {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  search?: string;
}

export interface IntegrationProfileListResponse {
  data: IntegrationProfileApiRow[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface CreateIntegrationProfileDto {
  profile_name: string;
  integration_type_id: number;
  status_id: number;
  environment: string;
  config_json?: IntegrationProfileConfig | null;
}

export interface UpdateIntegrationProfileDto {
  profile_name?: string;
  status_id?: number;
  environment?: string;
  config_json?: IntegrationProfileConfig | null;
}

export const listIntegrationProfiles = async (
  partyId: string,
  query: IntegrationProfileListQuery = {}
): Promise<{ success: boolean; data: IntegrationProfileListResponse }> => {
  const data = await ipRequest<IntegrationProfileListResponse>(
    partyId,
    buildQuery(query as Record<string, unknown>)
  );
  return { success: true, data };
};

export const getIntegrationProfile = async (
  partyId: string,
  id: string
): Promise<{ success: boolean; data: IntegrationProfileApiRow }> => {
  const data = await ipRequest<IntegrationProfileApiRow>(partyId, `/${id}`);
  return { success: true, data };
};

export const createIntegrationProfile = async (
  partyId: string,
  dto: CreateIntegrationProfileDto
): Promise<{ success: boolean; data: IntegrationProfileApiRow }> => {
  const data = await ipRequest<IntegrationProfileApiRow>(partyId, '', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
  return { success: true, data };
};

export const updateIntegrationProfile = async (
  partyId: string,
  id: string,
  dto: UpdateIntegrationProfileDto
): Promise<{ success: boolean; data: IntegrationProfileApiRow }> => {
  const data = await ipRequest<IntegrationProfileApiRow>(partyId, `/${id}`, {
    method: 'PUT',
    body: JSON.stringify(dto),
  });
  return { success: true, data };
};

export const deleteIntegrationProfile = async (
  partyId: string,
  id: string
): Promise<{ success: boolean }> => {
  await ipRequest<void>(partyId, `/${id}`, { method: 'DELETE' });
  return { success: true };
};
